import { createContext, memo, useEffect, useMemo, useState } from 'react';
import { getArticleByEntryUrl, getWikiCategoryData } from '@/services/introduction';
import CollapseList from '@/components/CollapseList';
import styles from '@/styles/pages/introduction-article.module.scss';
import IntroDetail from '@/components/IntroArticle';
import { useTranslation } from '@/hooks/use-translation';
import { Breadcrumb, ConfigProvider, message } from 'antd';
import Image from 'next/image';
import Head from 'next/head';
import { BreadcrumbList } from 'schema-dts';
import { jsonLdScriptProps } from 'react-schemaorg';
import Rating from '@/components/common/rate-component';
import { handleShare, shareMediaURl } from '@/utils/mediaShare';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { encryption } from '@/utils/encryption';
import http from '@/services/http';
import { useLanguage } from '@/hooks/use-language';
import dayJs from 'dayjs';
import { useRouter } from 'next/router';
import Link from 'next/link';
import classNames from 'classnames';
import Icon from '@ant-design/icons';
import ArrowSvg from '../../../../public/icon/LeftArrowOrang.svg';
import WhiteArrowSvg from '../../../../public/icon/LeftArrowWhite.svg';

export default memo((props: any) => {
  // const data = JSON.parse(props?.data);
  const articleData = props.articleData;
  // const { guideLandingPageDisplayCards } = props;
  // const initData = props.data;
  const [isRate, setIsRate] = useState(false);
  const [breadcrumbList, setBreadcrumbList] = useState<any[]>([]);
  const [ratingScore, setRatingScore] = useState(articleData?.blog_snippet?.blog_rating || 5.0);
  const [shareUrl, setShareUrl] = useState('');
  const t = useTranslation();
  const language = useLanguage();
  const luanuageType = useLanguage() === 'zh' ? 'zh-hk' : '';
  const router = useRouter();

  const { prevArticle, nextArticle } = useMemo(() => {
    const articleList: any[] = [];
    articleData?.table_of_content?.[0]?.table_of_content_entry?.forEach(
      (item: { blog_post: any[] }) => {
        item.blog_post.forEach(subItem => {
          articleList.push({
            title: subItem?.blog_title || subItem.blog[0]?.title,
            description: subItem.blog[0]?.blog_snippet?.description,
            url: subItem.blog[0]?.url
          });
        });
      }
    );
    const curArticle = articleData?.url;
    const curArticleIndex = articleList.findIndex(item => {
      return item?.url === curArticle;
    });
    return {
      prevArticle: articleList[curArticleIndex - 1],
      nextArticle: articleList[curArticleIndex + 1]
    };
  }, [articleData]);

  const setRateForArticle = async (score: number) => {
    const { url, uid, title, blog_snippet } = articleData;
    if (!url) return false;

    const { author, category, product_tags, topic_tags } = blog_snippet;

    setIsRate(true);

    const fpPromise = FingerprintJS.load();
    const fp = await fpPromise;
    const fpResult = await fp.get();
    const visitorId = fpResult.visitorId;

    const inputText = url + ':' + luanuageType + ':' + score + ':' + fpResult.visitorId;

    const formKey = encryption(inputText);

    const data = {
      pageUrl: url,
      entryUid: uid,
      contentType: 'blog_wiki_article_page',
      rating: score,
      blogTitle: title,
      author: author[0]?.title || '',
      category: category[0]?.title || '',
      productTag: product_tags ? product_tags[0]?.title : '',
      topicTag: topic_tags ? topic_tags[0]?.title : '',
      language: luanuageType,
      visitorId,
      formKey
    };
    const result = await http({}).post('/article/rate', data);
    setIsRate(true);
    setRatingScore(score);
    localStorage.setItem(articleData.uid, 'true');
    if (result.data) {
      setRatingScore(result.data.rating_score);
    }

    window?.dataLayer?.push({
      event: 'buttonClick',
      buttonTitle: 'rate_article',
      description: score,
      destinationUrl: '',
      pageURL: window.location.href,
      componentName: 'article rating'
    });
  };

  const shareArticle = (event: any, type: string) => {
    window?.dataLayer?.push({
      event: 'buttonClick',
      buttonTitle: 'share_article',
      description: type,
      destinationUrl: '',
      pageURL: shareUrl,
      componentName: 'article share'
    });
    handleShare(event, type);
  };

  const copyLink = () => {
    message.config({
      top: 0,
      duration: 20
    });
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => {
        message.success(t('copy_success'));
      })
      .catch(() => {
        message.error(t('copy_fail'));
      });
  };

  // 埋点
  const toTargetPage = (targetPage: { title: string }) => {
    window?.dataLayer?.push({
      event: 'buttonClick',
      buttonTitle: 'content_nav_article',
      description: targetPage?.title,
      pageURL: window.location.href,
      componentName: 'content nav'
    });
  };

  // 埋点
  const handleGA = (title: any, href: any) => {
    window?.dataLayer?.push({
      event: 'buttonClick',
      buttonTitle: 'guide_menu_bar',
      description: title,
      destinationUrl: href,
      pageURL: window.location.href,
      componentName: 'breadcrumb'
    });
  };

  useEffect(() => {
    if (typeof window !== 'undefined' && articleData) {
      const rateStatus = JSON.parse(localStorage.getItem(articleData.uid) || 'false');
      setIsRate(rateStatus);
    }
    setShareUrl(window.location.href);
  }, []);

  // Wiki Article Page BreadCrumb shema
  // Need to add this breadcrumb list to show breadcrumb schema in <Head>
  useEffect(() => {
    let list = [
      {
        name: language == 'zh' ? '首頁' : 'Home',
        link: language == 'zh' ? 'https://www.fwd.com.hk/' : 'https://www.fwd.com.hk/en/'
      },
      {
        name: language == 'zh' ? '網誌' : 'Blog',
        link: window.location.origin + (language == 'zh' ? '/zh/blog/' : '/en/blog/')
      },
      {
        name: language == 'zh' ? '超基本保險教室' : 'Guide',
        link: window.location.origin + (language == 'zh' ? '/zh/blog/guide/' : '/en/blog/guide/')
      },
      {
        name: articleData?.title,
        link:
          window.location.origin +
          (language == 'zh'
            ? `/zh/blog/guide${articleData?.url}`
            : `/en/blog/guide${articleData?.url}`)
      }
    ];

    setBreadcrumbList(list);
  }, []);

  // 埋点
  useEffect(() => {
    setTimeout(() => {
      window?.dataLayer?.push({
        event: 'content_landing',
        author:
          articleData?.blog_snippet?.author.length > 0
            ? articleData?.blog_snippet?.author[0]?.title
            : '',
        description:
          articleData?.locale == 'zh-hk'
            ? articleData?.blog_snippet?.description?.substring(0, 20)
            : articleData?.blog_snippet?.description?.substring(0, 40),
        publish_time: articleData?.publish_details?.time,
        last_edit_time: articleData?.updated_at,
        subtitle: '',
        tags: '',
        title: articleData?.blog_snippet?.blog_title,
        content_Group:
          articleData?.locale == 'zh-hk'
            ? '文章|' +
              (articleData?.blog_snippet?.category.length > 0
                ? articleData?.blog_snippet?.category?.[0]?.title
                : '')
            : 'article|' +
              (articleData?.blog_snippet?.category.length > 0
                ? articleData?.blog_snippet?.category?.[0]?.title
                : ''),
        pageURL: window.location.href
      });
    }, 1000);
  }, []);

  return (
    <div className={styles.wrapper}>
      <div className={styles.main_wrapper}>
        <div className={styles.breadcrumb_wrapper}>
          <ConfigProvider
            theme={{
              components: {
                Breadcrumb: {
                  separatorColor: '#183028',
                  linkColor: '#E87722',
                  linkHoverColor: '#E87722',
                },
              },
            }}
          >
            <Breadcrumb
              separator={
                <Image
                  src="/blog/icon/BreadIcon.svg"
                  className={classNames(styles.Bread_Icon, language === 'zh' ? '' : styles.Bread_Icon_EN)}
                  alt={''}
                  width={16}
                  height={16}
                />
              }
              className={classNames(styles.Breadcrumb, language === 'zh' ? styles.Breadcrumb_ZH : styles.Breadcrumb_EN)}
              items={[
                {
                  type: 'separator',
                  separator: (
                    <div
                      onClick={() => {
                        router.push(`/zh/blog/guide/`);
                      }}
                    >
                      <Image
                        src="/blog/icon/ArrowLeft.svg"
                        className={classNames(styles.Customer_Svg, language === 'zh' ? '' : styles.Customer_Svg_EN)}
                        alt={''}
                        width={18}
                        height={16}
                      />
                    </div>
                  ),
                },
                {
                  title: (
                    <a
                      href="https://www.fwd.com.hk/"
                      target={'_blank'}
                      onClick={() => handleGA('首頁', 'https://www.fwd.com.hk/')}
                      className={styles.breadcrumb_item}
                    >
                      首頁
                    </a>
                  ),
                },
                {
                  title: (
                    <a
                      href="/zh/blog/"
                      onClick={() => handleGA('網誌', '/zh/blog/')}
                      className={styles.breadcrumb_item}
                    >
                      網誌
                    </a>
                  ),
                },
                {
                  title: (
                    <a
                      href="/zh/blog/guide/"
                      onClick={() => handleGA('超基本保險教室', '/zh/blog/guide/')}
                      className={styles.breadcrumb_item}
                    >
                      超基本保險教室
                    </a>
                  ),
                },
                // 不知道为什么，breadcrumb的最后一个样式是display：none，所以加多一个
                {
                  title: '',
                },
              ]}
            ></Breadcrumb>
          </ConfigProvider>
        </div>
        <div className={styles.content_wrapper}>
          <CollapseList data={articleData?.table_of_content?.[0]} />
          <div className={styles.intro_wrapper} id="intro_wrapper">
            <IntroDetail data={articleData} ratingScore={ratingScore}></IntroDetail>
            {/* star 评分 */}
            <div className={styles.rate_wrapper}>
              <div className={styles.first_line_wrapper}>
                <p className={styles.rate_title}>{t('rating_title')}</p>
                <div className={styles.score_wrapper}>
                  <Image src="/blog/icon/Star.svg" className={styles.star} alt={''} width={25} height={25} />
                  <span className={styles.score_count}>{ratingScore.toFixed(1)}</span>
                </div>
              </div>
              <div className={styles.second_line_wrapper}>
                <p className={styles.rate_tip}>{isRate ? t('thanks_for_rate') : t('click_rate')}</p>
                {isRate ? (
                  <Image src="/blog/icon/Check.svg" alt={''} width={131} height={40} />
                ) : (
                  <Rating disabled={isRate} setRating={setRateForArticle} />
                )}
              </div>
            </div>
            {/* 刊登日期及分享文章等 */}
            <div className={styles.tip_wrapper}>
              <div className={styles.date_wrapper}>
                <p>
                  <span className={styles.date_title}>刊登日期 </span>
                  <span className={styles.date}>
                    : {dayJs(articleData?.blog_snippet?.post_date).format(t('fomat_YYMMDD'))}
                  </span>
                </p>
                <p>
                  <span className={styles.date_title}>更新日期 </span>
                  <span className={styles.date}>
                    : {dayJs(articleData?.blog_snippet?.last_update_date).format(t('fomat_YYMMDD'))}
                  </span>
                </p>
              </div>

              <div className={styles.share_wrapper}>
                <span className={styles.share_title}>分享文章</span>
                <a
                  href={shareMediaURl('facebook', shareUrl)}
                  target="_blank"
                  onClick={e => shareArticle(e, 'facebook')}
                  rel="noopener noreferrer"
                >
                  <Image
                    src="/blog/icon/FacebookDarkgreen.svg"
                    className={styles.share_icon}
                    alt={''}
                    width={25}
                    height={25}
                  />
                </a>
                <a
                  href={shareMediaURl('whatsapp', shareUrl)}
                  target="_blank"
                  onClick={e => shareArticle(e, 'whatsapp')}
                  rel="noopener noreferrer"
                >
                  <Image
                    src="/blog/icon/WhatsappDarkgreen.svg"
                    className={styles.share_icon}
                    alt={''}
                    width={25}
                    height={25}
                  />
                </a>
                <a
                  href={shareMediaURl('linkedin', shareUrl)}
                  target="_blank"
                  onClick={e => shareArticle(e, 'linkedin')}
                  rel="noopener noreferrer"
                >
                  <Image
                    src="/blog/icon/LinkedinDarkgreen.svg"
                    className={styles.share_icon}
                    alt={''}
                    width={23}
                    height={23}
                  />
                </a>
                <Image
                  src="/blog/icon/CopyDarkgreen.svg"
                  className={styles.share_icon}
                  alt={''}
                  width={25}
                  height={25}
                  onClick={() => copyLink()}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.prev_next_wrapper}>
        {prevArticle && (
          <Link href={{ pathname: `/${language}/blog/guide${prevArticle?.url}` }}>
            <div
              className={styles.prev_wrapper}
              onClick={() => {
                toTargetPage(prevArticle);
              }}
            >
              <div className={styles.first_line}>
                <Icon component={ArrowSvg} className={styles.prev_arrow} style={{ fontSize: '31px' }} />
                <Icon component={WhiteArrowSvg} className={styles.prev_arrow_hover} style={{ fontSize: '31px' }} />
                <p className={styles.prev_label}>上一章</p>
              </div>
              <p className={styles.prev_title}>{prevArticle?.title}</p>
              <p className={styles.prev_subscription}>{prevArticle.description}</p>
            </div>
          </Link>
        )}
        {nextArticle && (
          <Link href={{ pathname: `/${language}/blog/guide${nextArticle?.url}` }}>
            <div
              className={styles.next_wrapper}
              onClick={() => {
                toTargetPage(nextArticle);
              }}
            >
              <div className={styles.first_line}>
                <p className={styles.next_label}>下一章</p>
                <Icon component={ArrowSvg} className={styles.next_arrow} style={{ fontSize: '31px' }} />
                <Icon component={WhiteArrowSvg} className={styles.next_arrow_hover} style={{ fontSize: '31px' }} />
              </div>
              <p className={styles.next_title}>{nextArticle?.title}</p>
              <p className={styles.next_subscription}>{nextArticle.description}</p>
            </div>
          </Link>
        )}
      </div>

      <Head>
        <>
          {breadcrumbList?.length > 0 ? (
            <script
              {...jsonLdScriptProps<BreadcrumbList>({
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: breadcrumbList?.map((item: any, index: number) => {
                  return {
                    '@type': 'ListItem',
                    position: index + 1,
                    name: item.name,
                    item: item.link,
                  };
                }),
              })}
            />
          ) : (
            ''
          )}
        </>
      </Head>
    </div>
  );
});
